import {
    ASSISTANT_TOOL_NAMES,
    ASSISTANT_TOOL_OUTPUT_STATUSES,
    ASSISTANT_TOOL_OUTPUT_TYPES,
} from "~components/outfitter-assistant/assistant-tool-constants"
import {
    getOutputRecord,
    isRecord,
    type AnyToolUIPart,
} from "~components/shared/chat/tool-ui-part-helpers"

export interface AssistantChartPresentation {
    readonly title: string
    readonly description?: string
    readonly chart: AssistantChart
}

interface AssistantChart {
    readonly definition?: {
        readonly title?: string | null
        readonly description?: string | null
        readonly chart_type?: string | null
    } | null
    readonly data: AssistantSeriesChartData
}

interface AssistantSeriesChartData {
    readonly __typename: "SeriesChartData"
    readonly value_format?: string | null
    readonly x_axis_label?: string | null
    readonly y_axis_label?: string | null
    readonly show_legend?: boolean | null
    readonly series: readonly AssistantChartSeries[]
}

interface AssistantChartSeries {
    readonly name: string
    readonly points: readonly AssistantChartPoint[]
    readonly color?: string | null
    readonly is_dashed?: boolean | null
}

interface AssistantChartPoint {
    readonly label: string
    readonly value: number
    readonly timestamp?: number | null
}

export function getAssistantChartPresentation(
    part: AnyToolUIPart
): AssistantChartPresentation | null {
    if (part.type !== `tool-${ASSISTANT_TOOL_NAMES.PRESENT_CHART}`) return null

    const output = getOutputRecord(part)
    if (!isChartSuccessOutput(output)) return null

    const chart = parseChart(output.chart)
    if (!chart) return null

    return {
        chart,
        title: toOptionalString(chart.definition?.title) ?? "Chart",
        description: toOptionalString(chart.definition?.description),
    }
}

function isChartSuccessOutput(
    output: Readonly<Record<string, unknown>> | null
): output is Readonly<Record<string, unknown>> {
    if (!output) return false

    return (
        output.type === ASSISTANT_TOOL_OUTPUT_TYPES.CHART &&
        output.status === ASSISTANT_TOOL_OUTPUT_STATUSES.SUCCESS
    )
}

function parseChart(value: unknown): AssistantChart | null {
    if (!isRecord(value)) return null

    const data = parseSeriesChartData(value.data)
    if (!data) return null

    const definition = isRecord(value.definition)
        ? {
              title: toOptionalString(value.definition.title),
              description: toOptionalString(value.definition.description),
              chart_type: toOptionalString(value.definition.chart_type),
          }
        : null

    return { definition, data }
}

function parseSeriesChartData(value: unknown): AssistantSeriesChartData | null {
    if (!isRecord(value) || value.__typename !== "SeriesChartData") return null
    if (!Array.isArray(value.series)) return null

    const series = value.series.flatMap(parseChartSeries)
    if (!series.length) return null

    return {
        __typename: "SeriesChartData",
        series,
        value_format: toOptionalString(value.value_format),
        x_axis_label: toOptionalString(value.x_axis_label),
        y_axis_label: toOptionalString(value.y_axis_label),
        show_legend:
            typeof value.show_legend === "boolean"
                ? value.show_legend
                : undefined,
    }
}

function parseChartSeries(value: unknown): AssistantChartSeries[] {
    if (!isRecord(value)) return []

    const name = toOptionalString(value.name)
    if (!name || !Array.isArray(value.points)) return []

    const points = value.points.flatMap(parseChartPoint)
    if (!points.length) return []

    return [
        {
            name,
            points,
            color: toOptionalString(value.color),
            is_dashed:
                typeof value.is_dashed === "boolean"
                    ? value.is_dashed
                    : undefined,
        },
    ]
}

function parseChartPoint(value: unknown): AssistantChartPoint[] {
    if (!isRecord(value)) return []

    const label = toOptionalString(value.label)
    const parsedValue = typeof value.value === "number" ? value.value : null
    if (!label || parsedValue === null) return []

    return [
        {
            label,
            value: parsedValue,
            timestamp:
                typeof value.timestamp === "number"
                    ? value.timestamp
                    : undefined,
        },
    ]
}

function toOptionalString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value.trim() : undefined
}
